import React from "react";
import Select from "react-select";
import { Col, FormGroup, Input, Label } from "reactstrap";
import { toPageOptions } from "../PagesComponent/dropdownOptions";

type PageOption = ReturnType<typeof toPageOptions>[number];

export type LinkValue = { name?: string; link?: string };

export type LinkFieldProps = {
  settingVar: { variable: string; value?: LinkValue };
  onChange: (name: string, value: LinkValue) => void;
  linkPlaceholder?: string;
  pages?: Parameters<typeof toPageOptions>[0];
};

/**
 * Link building block (SCRUM-553, VariableField registry) — Name + Link
 * inputs building one `{name, link}` object incrementally via
 * `onChange(name, value)` (legacy handleLinkInput* payload: spread of the
 * current value with the edited half replaced).
 *
 * The `|| ""` value fallbacks keep the inputs controlled when the stored
 * value is undefined (e.g. right after a template switch) — without them the
 * input flips uncontrolled and keeps showing stale text that is no longer in
 * state (SCRUM-643 review fix).
 *
 * Internal-page picker (SCRUM-728): when the host passes the pages slice via
 * fieldProps, a searchable select of the site's PUBLISHED pages writes the
 * chosen page's own `page_configuration.path` into the `link` half. Notes:
 *
 *  - It writes through the SAME `onChange(variable, {...value, link})` the
 *    text input uses, so the per-half merge contract the update screen
 *    reconstructs (linkAwareValueChange (PagesComponent/contentVariableHelpers.ts) → handleLinkInputRequired)
 *    keeps working untouched — the picker is not a second write path.
 *  - The manual input stays the primary control and is never disabled: an
 *    external URL is still typed by hand, which is half of what this field is
 *    for.
 *  - The select's value is a REVERSE LOOKUP by path (first match wins), the
 *    same derivation CategoryMenuField uses for its page select. So typing a
 *    URL that matches no page simply returns the select to its placeholder,
 *    and no state is duplicated between the two controls.
 *  - No pages passed (every other host) → no select renders at all, so the
 *    field is byte-identical to its pre-SCRUM-728 self on those surfaces.
 */
export default function LinkField({
  settingVar,
  onChange,
  linkPlaceholder = "link",
  pages = [],
}: LinkFieldProps) {
  const setHalf =
    (half: keyof LinkValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange(settingVar.variable, { ...settingVar.value, [half]: e.target.value });

  const pageOptions = toPageOptions(pages);
  const currentLink = settingVar.value?.link || "";
  const selectedPage =
    pageOptions.find(
      (option) => option.value?.page_configuration?.path === currentLink
    ) ?? null;

  const pickPage = (selection: PageOption | null) =>
    onChange(settingVar.variable, {
      ...settingVar.value,
      link: selection?.value?.page_configuration?.path ?? "",
    });

  return (
    <>
    <FormGroup className="row">
      <Label
        className="form-control-label"
        htmlFor="example-text-input"
        md="2"
        style={{ color: "#597ade" }}
      >
        Name
      </Label>
      <Col md="4">
        <Input
          value={settingVar.value?.name || ""}
          className="form-control-sm"
          placeholder="name"
          type="text"
          name="name"
          onChange={setHalf("name")}
        />
      </Col>
      <Label
        className="form-control-label"
        htmlFor="example-text-input"
        md="2"
        style={{ color: "#597ade" }}
      >
        Link
      </Label>
      <Col md="4">
        <Input
          value={settingVar.value?.link || ""}
          className="form-control-sm"
          placeholder={linkPlaceholder}
          name="link"
          type="text"
          onChange={setHalf("link")}
        />
      </Col>
    </FormGroup>
    {pageOptions.length > 0 && (
      <FormGroup className="row">
        <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="2"
          style={{ color: "#597ade" }}
        >
          Internal page
        </Label>
        <Col md="10">
          <Select
            aria-label={`internal page for ${settingVar.variable}`}
            value={selectedPage}
            name={`${settingVar.variable}-internal-page`}
            options={pageOptions}
            onChange={pickPage}
            placeholder="Select an internal page…"
            isSearchable={true}
          />
        </Col>
      </FormGroup>
    )}
    </>
  );
}
